// Box-select (05d, main.js's marquee — Ben's #173 + #186): drag from empty canvas to draw
// a box; every module it touches is selected. Shift held at the press adds to the
// selection instead of replacing it. A plain click on empty canvas clears the selection
// (Shift+click keeps it). Desktop only, as in main.js.

import { useEffect } from "react";
import { useProtoStore } from "../store/store.js";
import { moduleElementOf } from "./module-registry.js";
import { BOX_DRAG_THRESHOLD, boxBetween, boxSelection, selectIds } from "./selection.js";

export function useBoxSelect(workspace: HTMLElement): void {
  useEffect(() => {
    function onPointerDown(e: PointerEvent): void {
      if (window.innerWidth <= 768) return;
      if (e.button !== 0) return;
      if ((e.target as Element).closest(".placed-module")) return;

      const startX = e.clientX;
      const startY = e.clientY;
      // Captured now, so letting go of Shift mid-drag doesn't change it.
      const keep = new Set(e.shiftKey ? useProtoStore.getState().selectedIds : []);

      const marquee = document.createElement("div");
      marquee.id = "selection-marquee";
      document.body.appendChild(marquee);
      marquee.style.left = `${startX}px`;
      marquee.style.top = `${startY}px`;
      marquee.style.width = "0px";
      marquee.style.height = "0px";

      const onMove = (ev: PointerEvent) => {
        const box = boxBetween(startX, startY, ev.clientX, ev.clientY);
        marquee.style.left = `${box.left}px`;
        marquee.style.top = `${box.top}px`;
        marquee.style.width = `${box.right - box.left}px`;
        marquee.style.height = `${box.bottom - box.top}px`;
        const modules = useProtoStore.getState().modules.flatMap((m) => {
          const el = moduleElementOf(m.id);
          return el ? [{ id: m.id, rect: el.getBoundingClientRect() }] : [];
        });
        selectIds(boxSelection(box, modules, keep));
      };

      const onUp = (ev: PointerEvent) => {
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onUp);
        marquee.remove();
        // A click, not a box: clear the selection, keeping only what Shift kept (so a
        // slightly-missed Shift+drag doesn't wipe what you'd built up). What the tiny box
        // touched is undone either way.
        if (Math.hypot(ev.clientX - startX, ev.clientY - startY) < BOX_DRAG_THRESHOLD) selectIds([...keep]);
      };

      document.addEventListener("pointermove", onMove);
      document.addEventListener("pointerup", onUp);
    }

    workspace.addEventListener("pointerdown", onPointerDown);
    return () => workspace.removeEventListener("pointerdown", onPointerDown);
  }, [workspace]);
}
